/** Per-user page permissions — mirrors backend/utils/pagePermissions.js */

import {
  EVENT_ACCESS_MODULES,
  expandLegacyPages,
  canAccessModule as canEventAccessModule,
} from "./eventAccessModules";

export const PAGE_SPECIAL_ACTIONS = {
  "occasion-master": "Activate / Deactivate",
};

export const rolePresetFlags = (role) => {
  if (role === "Manager" || role === "Admin") {
    return { view: true, edit: true, delete: true, specialActions: true };
  }
  if (role === "Editor") {
    return { view: true, edit: true, delete: false, specialActions: false };
  }
  return { view: true, edit: false, delete: false, specialActions: false };
};

export const buildDefaultPagePermissions = (allowedPageKeys, role = "Viewer") => {
  const allowed = new Set(expandLegacyPages(allowedPageKeys || []));
  const flags = rolePresetFlags(role);
  return EVENT_ACCESS_MODULES.filter(
    (m) => m.key !== "event-user" && allowed.has(m.key)
  ).map((m) => ({
    pageKey: m.key,
    view: m.alwaysOn ? true : flags.view,
    edit: m.alwaysOn ? false : flags.edit,
    delete: m.alwaysOn ? false : flags.delete,
    specialActions:
      m.alwaysOn || !PAGE_SPECIAL_ACTIONS[m.key] ? false : flags.specialActions,
  }));
};

const fullPerm = (pageKey) => ({
  view: true,
  edit: pageKey !== "dashboard",
  delete: pageKey !== "dashboard",
  specialActions: Boolean(PAGE_SPECIAL_ACTIONS[pageKey]),
});

const denyPerm = () => ({
  view: false,
  edit: false,
  delete: false,
  specialActions: false,
});

/**
 * Effective CRUD flags for a page for the current user/event session.
 */
export const getPagePermission = (selectedEvent, pageKey, user) => {
  if (user?.isSuperAdmin) return fullPerm(pageKey);
  if (selectedEvent?.isAdmin || user?.role === "Admin") return fullPerm(pageKey);

  // Must be allowed at event (Super Admin Role Access) first
  if (!canEventAccessModule(selectedEvent, pageKey, user) && pageKey !== "dashboard") {
    return denyPerm();
  }

  const list = selectedEvent?.pagePermissions;
  if (Array.isArray(list) && list.length) {
    const row = list.find((p) => p.pageKey === pageKey);
    if (!row || !row.view) return denyPerm();
    return {
      view: true,
      edit: Boolean(row.edit),
      delete: Boolean(row.delete),
      specialActions: Boolean(row.specialActions) && Boolean(PAGE_SPECIAL_ACTIONS[pageKey]),
    };
  }

  // Legacy: Manager / Editor / Viewer
  const role = selectedEvent?.role || user?.role;
  const flags = rolePresetFlags(role);
  return {
    view: true,
    edit: flags.edit,
    delete: flags.delete,
    specialActions: flags.specialActions && Boolean(PAGE_SPECIAL_ACTIONS[pageKey]),
  };
};

export const canViewPage = (selectedEvent, pageKey, user) =>
  getPagePermission(selectedEvent, pageKey, user).view;

export const canEditPage = (selectedEvent, pageKey, user) =>
  getPagePermission(selectedEvent, pageKey, user).edit;

export const canDeletePage = (selectedEvent, pageKey, user) =>
  getPagePermission(selectedEvent, pageKey, user).delete;

export const canSpecialAction = (selectedEvent, pageKey, user) =>
  getPagePermission(selectedEvent, pageKey, user).specialActions;

/** Modules assignable on User Access (exclude self-management). */
export const USER_ACCESS_MODULES = EVENT_ACCESS_MODULES.filter(
  (m) => m.key !== "event-user"
);
